import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Page } from "@app/components/page";
import { Button, Pagination } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import SearchCriteria from "@app/components/adverse-events/search-criteria";
import Result from "@app/components/adverse-events/result";
import EmptyList from "@app/components/list/empty";
import notify from "@app/components/notify";
import { download } from "@app/lib/file";
import { ws } from "@app/lib/socket";
import SyncWarning from "../sync-warning";
import { NavLink } from "react-router-dom";

import adverseEvents from "@app/state/store/report/adverse-events";

const AdverseEventsPage = observer((props) => {
    const hasSyncState = adverseEvents?.synchronizing !== undefined;
    const [focused, setFocused] = useState(null);
    const [downloadHasBeenQueued, setDownloadHasBeenQueued] = useState(false);

    useEffect(() => {
        adverseEvents.loadSyncState();
    }, []);

    useEffect(() => {
        ws.on("maudeReport.queuedDownload.ready", (data) => {
            notify.success("Your download is ready and will begin automatically");
            download(data._id, data.file);
            setDownloadHasBeenQueued(false);
        });

        return () => {
            ws.removeAllListeners(["maudeReport.queuedDownload.ready"]);
        };
    }, [downloadHasBeenQueued]);

    const focus = (key) => {
        setFocused(key);
    };

    const onPageChange = (newPage) => {
        adverseEvents.pager.currentPage = newPage;
        adverseEvents.loadEvents();
    };

    /**
     * Render the search results
     */
    const Results = observer(() => {
        if (!adverseEvents.loaded) {
            return null;
        }

        if (adverseEvents.results.length === 0) {
            return (
                <EmptyList title="No Results Found" show={!adverseEvents.loading}>
                    No results were found using the search criteria.
                </EmptyList>
            );
        }

        return (
            <div className="results">
                {adverseEvents.results.map((event, idx) => {
                    return (
                        <NavLink
                            to={`./adverse-events/${event.key}`}
                            key={event.key}
                            onClick={() => focus(event.key)}
                        >
                            <Result event={event} focus={event.key === focused} />
                        </NavLink>
                    );
                })}
            </div>
        );
    });

    /**
     * Render the button for exporting the complete result set
     */
    const ExportButton = observer(() => {
        // only show the export button when their are events to export
        if (adverseEvents.results.length === 0) {
            return null;
        }

        const generateReport = async () => {
            const reportQueueResponse = await adverseEvents.generateReport();
            setDownloadHasBeenQueued(reportQueueResponse.isQueued);
            notify.info(
                "Your download has been queued and will begin automatically when ready. Don't navigate away!",
            );
        };

        return (
            <>
                {downloadHasBeenQueued ? (
                    <Button
                        icon={<FileExcelOutlined />}
                        disabled={true}
                        loading={downloadHasBeenQueued}
                    >
                        Generating file for export...
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        onClick={generateReport}
                        icon={<FileExcelOutlined />}
                        disabled={!adverseEvents.exportable}
                        loading={adverseEvents.exporting}
                    >
                        Export
                    </Button>
                )}
            </>
        );
    });

    const Pager = observer(() => {
        if (!adverseEvents.results || adverseEvents.results.length === 0) {
            return null;
        }

        return (
            <div className="pager">
                <Pagination
                    current={adverseEvents.pager.currentPage}
                    pageSize={adverseEvents.pager.pageSize}
                    showSizeChanger={false}
                    onChange={onPageChange}
                    total={adverseEvents.totalCount}
                />
            </div>
        );
    });

    return (
        <Page className="adverse-events">
            <Page.Header title="MAUDE">
                <Page.Header.Right shrink>
                    <ExportButton />
                </Page.Header.Right>
            </Page.Header>

            <Page.Layout>
                <Page.Body>
                    {hasSyncState && (
                        <>
                            {adverseEvents.synchronizing && <SyncWarning />}
                            {!adverseEvents.synchronizing && (
                                <>
                                    <SearchCriteria />
                                    <Results />
                                </>
                            )}
                        </>
                    )}
                </Page.Body>
            </Page.Layout>

            <Page.Footer>
                <Page.Footer.Left>
                    <Pager />
                </Page.Footer.Left>
                <Page.Footer.Right>
                    {adverseEvents.totalCount ? <>{adverseEvents.totalCount} results</> : ""}
                </Page.Footer.Right>
            </Page.Footer>
        </Page>
    );
});

export default AdverseEventsPage;
